import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BookOpen, Bell, BellOff, CheckCircle2 } from 'lucide-react';
import { AwardWithBooks } from '@/types/awards';
import { CoverImage } from './CoverImage';
import { format } from 'date-fns';
import { useFollowAward, useUnfollowAward, useAwardFollows } from '@/hooks/useAwardFollows';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface AwardCardProps {
  award: AwardWithBooks;
}

export function AwardCard({ award }: AwardCardProps) {
  const { user } = useAuth();
  const { data: follows = [] } = useAwardFollows();
  const followAward = useFollowAward();
  const unfollowAward = useUnfollowAward();

  // Fetch user's read books
  const { data: readBooks } = useQuery({
    queryKey: ['user-read-books', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('user_books')
        .select('book_id')
        .eq('user_id', user.id)
        .eq('shelf', 'read');
      
      if (error) throw error;
      return data?.map(ub => ub.book_id) || [];
    },
    enabled: !!user?.id,
  });

  const readBookIds = new Set(readBooks || []);
  const isFollowing = follows.some(f => f.prize_name === award.prize_name);
  const shortlistMonth = award.shortlist_date 
    ? format(new Date(award.shortlist_date), 'MMMM')
    : null;

  const handleFollowToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!user) {
      return;
    }
    if (isFollowing) {
      unfollowAward.mutate(award.prize_name);
    } else {
      followAward.mutate(award.prize_name);
    }
  };

  return (
    <Link to={`/awards/${award.id}`}>
      <Card className="w-[320px] flex-shrink-0 hover:shadow-lg transition-shadow cursor-pointer h-full">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <CardTitle className="text-lg line-clamp-2">{award.prize_name}</CardTitle>
              {shortlistMonth && (
                <p className="text-sm text-muted-foreground mt-1">
                  {award.genre_focus === 'lifetime' ? shortlistMonth : `${shortlistMonth} Shortlist`}
                </p>
              )}
            </div>
            {user && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleFollowToggle}
                className="flex-shrink-0"
              >
                {isFollowing ? (
                  <BellOff className="w-4 h-4" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
          <div className="flex gap-2 mt-2 flex-wrap">
            <Badge variant="secondary">{award.region}</Badge>
            <Badge variant="outline">{award.genre_focus}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {award.shortlist_books.length > 0 ? (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {award.shortlist_books.slice(0, 6).map((book) => (
                <div key={book.id} className="flex-shrink-0 relative">
                  <CoverImage
                    bookId={book.id}
                    title={book.title}
                    author={book.authors[0]}
                    coverUrl={book.cover_url}
                    size="sm"
                    className="w-16 h-24 rounded shadow-sm"
                  />
                  {readBookIds.has(book.id) && (
                    <div className="absolute inset-0 bg-green-600/30 rounded flex items-center justify-center">
                      <CheckCircle2 className="w-6 h-6 text-white drop-shadow-lg" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-24 text-muted-foreground">
              <BookOpen className="w-8 h-8 mr-2" />
              <span className="text-sm">
                {award.genre_focus === 'lifetime' ? 'Notable works coming soon' : 'Shortlist coming soon'}
              </span>
            </div>
          )}
          <Button variant="outline" className="w-full mt-3" size="sm">
            {award.genre_focus === 'lifetime' ? 'View Notable Works' : 'View Shortlist'}
          </Button>
        </CardContent>
      </Card>
    </Link>
  );
}
